import { useBrokersStore } from "./store";
import {
  Broker,
  BrokerAPIResponse,
  BrokerAPIResult,
  Server,
  VybeServerApiRow,
} from "./types";
import { SIGNALR_CONFIG } from "@/vybe-trader-client/connection/config/signalr";

const BROKER_API_URL =
  "https://search.mtapi.io/Search?company=allbrokers&mt5=true";

const buildLogoText = (company: string) =>
  company
    .split(" ")
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();

export const transformBrokerData = (apiData: BrokerAPIResult[]): Broker[] => {
  return apiData.map((broker) => {
    const firstServer = broker.results[0];
    const logoText = buildLogoText(broker.company);

    const servers: Server[] = broker.results.map((result) => ({
      id: `${broker.company}_${result.name}`,
      serverName: result.name,
      access: result.access || [],
    }));

    return {
      id: broker.company,
      name:
        broker.company.length > 35
          ? broker.company.slice(0, 35) + "..."
          : broker.company,
      fullName: broker.company,
      subtitle: firstServer?.name || "T5 Broker",
      logo: logoText,
      logoType: firstServer?.logo_url ? "image" : "text",
      address: "",
      url: firstServer?.site || "",
      servers,
      demo: false,
      mt5: true,
      logoUrl: firstServer?.logo_url,
      providerKind: "T5",
    };
  });
};

export const transformVybeServerData = (apiData: VybeServerApiRow[]): Broker[] => {
  const grouped = new Map<string, VybeServerApiRow[]>();

  for (const row of apiData) {
    const existing = grouped.get(row.companyName) ?? [];
    existing.push(row);
    grouped.set(row.companyName, existing);
  }

  return Array.from(grouped.entries()).map(([companyName, servers]) => {
    const firstServer = servers[0];
    const logoText = buildLogoText(companyName);

    return {
      id: `vybe:${companyName}`,
      name:
        companyName.length > 35
          ? companyName.slice(0, 35) + "..."
          : companyName,
      fullName: companyName,
      subtitle: `${servers.length} Vybe server${servers.length === 1 ? "" : "s"}`,
      logo: logoText,
      logoType: firstServer.iconUrl ? "image" : "text",
      address: "",
      url: firstServer.companyUrl || "",
      servers: servers.map((server) => ({
        id: `vybe:${companyName}_${server.serverName}`,
        serverName: server.serverName,
        access: ["vybe-managed"],
      })),
      demo: false,
      mt5: false,
      logoUrl: firstServer.iconUrl,
      providerKind: "Vybe",
    };
  });
};

export const useFetchBrokers = async (): Promise<Broker[]> => {
  try {
    const response = await fetch(BROKER_API_URL);

    if (!response.ok) {
      throw new Error(`Failed to fetch brokers: ${response.statusText}`);
    }

    const data: BrokerAPIResponse = await response.json();
    const brokersData = data.result || [];
    return transformBrokerData(brokersData);
  } catch (error) {
    console.error("Error fetching brokers:", error);
    throw error;
  }
};

export const fetchCustomBrokers = async (): Promise<Broker[]> => {
  const hubUrl = SIGNALR_CONFIG.HUB_URL;
  if (!hubUrl) {
    console.warn(
      "VITE_HUB_URL is not configured; skipping custom brokers."
    );
    return [];
  }

  try {
    const response = await fetch(`${hubUrl}/api/custom-brokers`);
    if (!response.ok) {
      throw new Error(`Failed to fetch custom brokers: ${response.statusText}`);
    }

    const data: BrokerAPIResponse = await response.json();
    const brokersData = data.result || [];
    return transformBrokerData(brokersData);
  } catch (error) {
    console.error("Error fetching custom brokers:", error);
    return [];
  }
};

export const fetchTradingGateway = async (): Promise<Broker[]> => {
  const hubUrl = SIGNALR_CONFIG.HUB_URL;
  if (!hubUrl) {
    console.warn(
      "VITE_HUB_URL is not configured; skipping trading gateway."
    );
    return [];
  }

  try {
    const response = await fetch(`${hubUrl}/api/trading-gateway`);
    if (!response.ok) {
      throw new Error(
        `Failed to fetch trading gateway: ${response.statusText}`
      );
    }

    const data: VybeServerApiRow[] = await response.json();
    return transformVybeServerData(data);
  } catch (error) {
    console.error("Error fetching trading gateway:", error);
    return [];
  }
};

/** @deprecated Use fetchTradingGateway */
export const fetchVybeServers = fetchTradingGateway;

export const mergeCustomBrokersIntoT5 = (
  t5Brokers: Broker[],
  customBrokers: Broker[]
): Broker[] => {
  const merged = t5Brokers.map((broker) => ({
    ...broker,
    servers: [...broker.servers],
  }));

  for (const custom of customBrokers) {
    const existing = merged.find(
      (broker) =>
        broker.fullName.toLowerCase() === custom.fullName.toLowerCase()
    );

    if (existing) {
      for (const server of custom.servers) {
        const alreadyExists = existing.servers.some(
          (candidate) => candidate.serverName === server.serverName
        );
        if (!alreadyExists) {
          existing.servers.push(server);
        }
      }

      if (custom.logoUrl && !existing.logoUrl) {
        existing.logoUrl = custom.logoUrl;
        existing.logoType = "image";
      }

      if (custom.url && !existing.url) {
        existing.url = custom.url;
      }
    } else {
      merged.push(custom);
    }
  }

  return merged;
};

export const initializeBrokersStore = () => {
  const { loadBrokers, setBrokers } = useBrokersStore.getState();

  loadBrokers();
  setBrokers();
};
